import { Button } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { PublicPage } from 'components/templates';
import { Form, Formik, FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { t } from 'i18next';
import { PiCaretLeft, PiShieldPlus } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoveryTwoFactor } from 'services/auth/recoveryTwoFactor';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import * as Yup from 'yup';

type FormValues = {
  email: string;
  code: string;
};

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const TwoFactorRecovery = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const email = query.get('email') || '';

  const validationSchema = Yup.object().shape({
    code: Yup.string()
      .required(t('formik-error.required'))
      .min(11, t('formik-error.minLength', { min: 11 }))
      .max(11, t('formik-error.maxLength', { max: 11 })),
  });

  const initialValues: FormValues = {
    email: email || '',
    code: '',
  };

  const recoveryTwoFactor = useRecoveryTwoFactor();
  const mutateOptions = useMutateOptions<FormValues>();

  const handleSubmit = async (
    values: FormValues,
    formikHelpers: FormikHelpers<FormValues>
  ) => {
    if (values?.code.length && email) {
      await recoveryTwoFactor.mutateAsync(
        { email: email, code: values.code },
        {
          ...mutateOptions(formikHelpers),
          onSuccess: () => navigate('/login'),
        }
      );
    }
  };

  return (
    <PublicPage
      header={
        <>
          <PiShieldPlus />
          {t('twoFactorRecovery.title')}
        </>
      }
      footer={
        <Button
          variant={BUTTON_VARIANT.white}
          onClick={() => navigate(-1)}>
          <Button.Icon icon={<PiCaretLeft />} />
          <Button.Text text={t('twoFactorRecovery.back')} />
        </Button>
      }>
      <div className='w-72 opacity-65'>{t('twoFactorRecovery.message')}</div>
      <Formik
        validateOnBlur
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}>
        {() => (
          <Form>
            <FormInput.Text
              name='code'
              placeholder='00000 00000'
              maxLength={11}
              label={t('input.label.recoveryCode')}
            />
            <Button type='submit' variant={BUTTON_VARIANT.submit} fullWidth>
              <Button.Text text={`Submit ${t('input.label.recoveryCode')}`} />
            </Button>
          </Form>
        )}
      </Formik>
    </PublicPage>
  );
};

export default TwoFactorRecovery;
